import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Package } from "lucide-react";
import toast from "react-hot-toast";
import {
  Button,
  Input,
  Select,
  Table,
  Pagination,
  Card,
  ConfirmModal,
  Badge,
} from "@/components/ui";
import { productsApi } from "@/services/api";

export default function ProductsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["products", { search, category, page }],
    queryFn: () =>
      productsApi.list({
        search,
        category_id:
          category && category !== "undefined" ? category : undefined,
        page,
        per_page: 10,
      }),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => productsApi.listCategories(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsApi.delete(id),
    onSuccess: () => {
      toast.success("Produto removido com sucesso");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setDeleteId(null);
    },
    onError: () => {
      toast.error("Erro ao remover produto");
    },
  });

  const products = Array.isArray(data?.data?.items)
    ? data?.data?.items
    : Array.isArray(data?.data)
      ? data?.data
      : [];
  const total = data?.data?.total || products.length;
  const totalPages = Math.ceil(total / 10);
  const categories = categoriesData?.data || [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const unitLabels: Record<string, string> = {
    unit: "Unidade",
    hour: "Hora",
    day: "Dia",
    week: "Semana",
    month: "Mês",
    year: "Ano",
    project: "Projeto",
    service: "Serviço",
    license: "Licença",
    user: "Usuário",
  };

  const columns = [
    {
      key: "name",
      header: "Produto",
      render: (product: any) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Package className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">{product.name}</p>
            {product.code && (
              <p className="text-sm text-text-muted font-mono">
                {product.code}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "category",
      header: "Categoria",
      render: (product: any) =>
        product.category_name ? (
          <Badge variant="neutral">{product.category_name}</Badge>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "price",
      header: "Preço Unitário",
      render: (product: any) => (
        <span className="font-mono">{formatCurrency(product.price)}</span>
      ),
    },
    {
      key: "unit",
      header: "Unidade",
      render: (product: any) => (
        <span className="text-text-secondary">
          {unitLabels[product.unit] || product.unit || "Unidade"}
        </span>
      ),
    },
    {
      key: "is_active",
      header: "Status",
      render: (product: any) => (
        <Badge variant={product.is_active ? "success" : "error"}>
          {product.is_active ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-20",
      render: (product: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/products/${product.id}/edit`);
            }}
            className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(product.id);
            }}
            className="p-2 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
            Produtos
          </h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">
            Gerencie seu catálogo de produtos e serviços
          </p>
        </div>
        <Link to="/admin/products/new" className="w-full sm:w-auto">
          <Button
            leftIcon={<Plus className="w-5 h-5" />}
            className="w-full sm:w-auto"
          >
            Novo Produto
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por nome ou SKU..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              leftIcon={<Search className="w-5 h-5" />}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "", label: "Todas as categorias" },
                ...categories.map((cat: any) => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Table
        columns={columns}
        data={products}
        keyExtractor={(product) => product.id}
        isLoading={isLoading}
        emptyMessage="Nenhum produto encontrado"
        onRowClick={(product) => navigate(`/admin/products/${product.id}/edit`)}
      />

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={total}
        itemsPerPage={10}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Remover Produto"
        message="Tem certeza que deseja remover este produto? Esta ação não pode ser desfeita."
        confirmText="Remover"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
